import { findChildren } from '@tiptap/core';
import { Plugin, PluginKey, PluginView } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { Node as ProsemirrorNode } from '@tiptap/pm/model';
import { Subject } from 'rxjs';

export function AnyncHighlightPlugin({ name, hlWorker, hlWorkerMessage }: { name: string, hlWorker: Worker, hlWorkerMessage: Subject<any> }) {
    const asyncHighlightPlugin: Plugin<any> = new Plugin({
        key: new PluginKey('asyncHighlightPlugin'),

        view(view) {
            // console.log('=[hl]=[AnyncHighlightPlugin]==>', 'view');
            // This small view is just for initial async handling
            class AnyncHighlightPluginView implements PluginView {
                count: number = 2;
                constructor() {
                    this.checkUndecoratedBlocks();
                }

                update() {
                    const tokens = view.state.tr.getMeta('allTokens')
                    // console.log('=[hl]=[AnyncHighlightPlugin]==> view update, docChanged:', view.state.tr.docChanged, 'count:', this.count, 'tokens:', tokens);
                    if (--this.count > 0 && (view.state.tr.docChanged || !tokens?.length)) {
                        // console.log('=[hl]=[AnyncHighlightPlugin]==> view update, count:', this.count);
                        this.checkUndecoratedBlocks();
                    }
                }

                destroy() { }

                async checkUndecoratedBlocks() {
                    // console.log('=[hl]=[AnyncHighlightPlugin]==> view constructor, checkUndecoratedBlocks, name:', name);
                    const codeBlocks = findChildren(
                        view.state.doc,
                        (node) => node.type.name === name,
                    );
                    // console.log('=[hl]=[AnyncHighlightPlugin]==> view constructor, checkUndecoratedBlocks, codeBlocks:', codeBlocks);

                    const allTokens = await Promise.all(
                        codeBlocks.flatMap(block => getHlTokens(
                            block.node.attrs.sid + '-' + block.pos,
                            hlWorker,
                            hlWorkerMessage,
                            block.node.textContent,
                            block.node.attrs.language
                        ))
                    );
                    // console.log('=[hl]=[AnyncHighlightPlugin]==>', 'view, checkUndecoratedBlocks allTokens:', allTokens);
                    if (allTokens?.length) {
                        const tr = view.state.tr.setMeta(
                            'allTokens',
                            allTokens.reduce((obj, cur) => ({
                                ...obj,
                                [cur.sid]: [getTokensArray(cur.tokens, '')]
                            }), {})
                        );
                        // console.log('=[hl]=[AnyncHighlightPlugin]==>', 'view, dispatch allTokens:', allTokens);
                        try {
                            view.dispatch(tr);
                        } catch {}
                    }
                }
            }

            return new AnyncHighlightPluginView()
        },

        state: {
            init: (_, { doc, tr }) => {
                // console.log('=[hl]=[AnyncHighlightPlugin]==>', 'init, allTokens:', tr.getMeta('allTokens'));
                return DecorationSet.create(doc, []);
            },
            apply: (transaction, decorationSet, oldState, newState) => {
                // console.groupCollapsed('=[hl]=[AnyncHighlightPlugin]==> apply');
                // console.log('\t transaction:', transaction);
                // console.log('\t decorationSet:', decorationSet);
                const oldNodeName = oldState.selection.$head.parent.type.name
                const newNodeName = newState.selection.$head.parent.type.name
                const oldNodes = findChildren(
                    oldState.doc,
                    (node) => node.type.name === name,
                )
                const newNodes = findChildren(
                    newState.doc,
                    (node) => node.type.name === name,
                )

                const didChangeSomeCodeBlock =
                    transaction.docChanged &&
                    // Apply decorations if:
                    // selection includes named node,
                    ([oldNodeName, newNodeName].includes(name) ||
                        // OR transaction adds/removes named node,
                        newNodes.length !== oldNodes.length ||
                        // OR transaction has changes that completely encapsulte a node
                        // (for example, a transaction that affects the entire document).
                        // Such transactions can happen during collab syncing via y-prosemirror, for example.
                        transaction.steps.some((step) => {
                            // @ts-ignore
                            return (
                                // @ts-ignore
                                step.from !== undefined &&
                                // @ts-ignore
                                step.to !== undefined &&
                                oldNodes.some((node) => {
                                    // @ts-ignore
                                    return (
                                        // @ts-ignore
                                        node.pos >= step.from &&
                                        // @ts-ignore
                                        node.pos + node.node.nodeSize <= step.to
                                    )
                                })
                            )
                        }))

                // only create code decoration when it's necessary to do so
                // if (
                //     transaction.getMeta('AnyncHighlightPluginForceDecoration') ||
                //     didChangeSomeCodeBlock
                // ) {
                //     return getDecorations({
                //         doc: transaction.doc,
                //         name,
                //         defaultLanguage,
                //     })
                // }

                const blockTokens = transaction.getMeta('allTokens');
                // console.log('\t didChangeSomeCodeBlock:', didChangeSomeCodeBlock);
                // console.log('\t blockTokens:', blockTokens);
                // console.groupEnd();
                if (blockTokens) {
                    return getDecorations({ doc: transaction.doc, name, blockTokens });
                }

                return decorationSet.map(transaction.mapping, transaction.doc);
            },
        },

        props: {
            decorations(state) {
                return asyncHighlightPlugin.getState(state);
            },
        },
    })

    return asyncHighlightPlugin;
}

type HlTokens = { content: string, css: string };
type HlTokensList = HlTokens[][];
type HlTreeItem = string | { children: HlTreeItem[], scope: string };
function getTokensArray(tree: HlTreeItem[], scope: string): HlTokens[] {
    const list: HlTokens[] = [];
    for (const item of tree) {
        if (typeof item == 'string') {
            list.push({ content: item, css: scopeToCSSClass(scope) });
        }
        else {
            list.push(...getTokensArray(item.children, item.scope));
        }
    }
    return list;
}

const scopeToCSSClass = (name: string) => {
    if (name.startsWith("language:")) {
        return name.replace("language:", "language-");
    }
    if (name.includes(".")) {
        const pieces = name.split(".");
        return [
            `hljs-${pieces.shift()}`,
            ...(pieces.map((x, i) => `${x}${"_".repeat(i + 1)}`))
        ].join(" ");
    }
    return `hljs-${name}`;
};

async function getHlTokens(
    sid: string,
    hlWorker: Worker,
    hlWorkerMessage: Subject<any>,
    text: string,
    language: string
): Promise<{ sid: string, tokens: HlTreeItem[] }> {
    return new Promise((resolve) => {
        const sub = hlWorkerMessage.subscribe(ev => {
            const { data } = ev;
            if (data.id == sid) {
                // console.log('=[hl]=[AnyncHighlightPlugin]==> getHlTokens', `<${sid}> getHlTokens.onmessage:`, data);
                sub.unsubscribe();
                resolve({ sid, tokens: data.tokens });
            }
        });
        // console.log('=[hl]=[AnyncHighlightPlugin]==> getHlTokens', `<${sid}> postMessage:`, { id: sid, text, language });
        hlWorker.postMessage({ id: sid, text, language });
    });
}

function getDecorations(
    { doc, name, blockTokens }: { doc: ProsemirrorNode, name: string, blockTokens: { [sid: string]: HlTokensList } }
) {
    // console.groupCollapsed('=[hl]=[AnyncHighlightPlugin]==> getDecorations');
    // console.log('=[hl]=[AnyncHighlightPlugin]==>', `getDecorations blockTokens:`, blockTokens);
    const decorations: Decoration[] = [];
    const codeBlocks = findChildren(doc, (node) => node.type.name === name);
    // console.log('=[hl]=[AnyncHighlightPlugin]==>', `getDecorations codeBlocks:`, codeBlocks, '\n\t name:', name, '\n\t doc:', doc.toJSON());
    codeBlocks.forEach((block) => {
        // console.log('=[hl]=[AnyncHighlightPlugin]==>', `getDecorations block:`, block);
        const tokens = blockTokens[block.node.attrs.sid + '-' + block.pos];
        // console.log('=[hl]=[AnyncHighlightPlugin]==>', `getDecorations tokens:`, tokens);
        if (tokens) {
            let from = block.pos + 1;
            for (const line of tokens) {
                // console.log('=[hl]=[AnyncHighlightPlugin]==>', `getDecorations line:`, line);
                for (const token of line) {
                    const to = from + token.content.length
                    if (token.css) {
                        const d = Decoration.inline(from, to, { class: token.css });
                        // console.log('=[hl]=[AnyncHighlightPlugin]==>', `getDecorations Decoration:`, d);
                        decorations.push(d);
                    }
                    from = to;
                }
                from += 1;
            }
        }
    })
    // console.log('=[hl]=[AnyncHighlightPlugin]==>', `getDecorations decorations:`, decorations);
    // console.groupEnd();

    return DecorationSet.create(doc, decorations);
}
